import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { ComponentParamaterType, ComponentType, MessageType } from '@adornis/whatsapp/server/types.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Einzelberatung } from '../entities/einzelberatung.js';
import { Institution } from '../entities/institution.js';
import { getEmailVarsFromEinzelberatung, getEmailVarsFromWorkshop } from '../entities/mail-helper.js';
import {
  getWhatsappTemplate,
  InstitutionMailTemplate,
  MailTemplateType,
  MailTemplateVars,
} from '../entities/mail-templates.js';
import { Product } from '../entities/products.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType, sendWhatsapp } from './_helper.js';

// Mail template for Product Decanceled

defineMailTemplate(MailTemplateType.PRODUCT_DECANCELED, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Event findet wieder statt',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>mit großer Freude können wir mitteilen, dass die ehemals abgesagte Veranstaltung "{{event-title}}" nun doch wieder stattfindet.</p>
<p>Weitere Angebote findet ihr unter: <a href="https://ics-rm.de" >https://ics-rm.de</a></p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>It is with great pleasure that we can announce that the previously canceled event "{event-title}" will now take place after all.</p>
<p>More events are available at: <a href="https://ics-rm.de" >https://ics-rm.de</a></p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: InstitutionMailTemplate.getAvailableVariables(MailTemplateType.PRODUCT_DECANCELED),
  attachments: [],
  displayName: 'Produkt wiederhergestellt',
  editorHint: () => InstitutionMailTemplate.getMailHint(MailTemplateType.PRODUCT_DECANCELED),
});

export async function notifyProductDecanceled(productID: string, userID: string) {
  const product = await getByID(
    Product,
    productID,
  )({
    _id: 1,
    name: 1,
    institutionID: 1,
    timeframe: { from: 1, till: 1 },
    appointments: { location: 1, start: 1, end: 1 },
    slots: { start: 1, end: 1 },
  });
  if (!product) throw new Error('could not find product ' + productID);

  const user = await getByID(
    User,
    userID,
  )({ _id: 1, firstName: 1, lastName: 1, title: 1, email: 1, whatsappNotification: 1, whatsappPhone: 1 });
  if (!user) throw new Error('could not find user ' + userID);

  const institution = await getByID(
    Institution,
    product.institutionID,
  )({
    _id: 1,
    name: 1,
    colorDefintions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) throw new Error('could not find institution');
  const institutionID = institution._id;

  const mailTemplateType = MailTemplateType.PRODUCT_DECANCELED;
  // set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = (await InstitutionMailTemplate.getMailTemplateForInstitution(
    institutionID,
    mailTemplateType,
  )(InstitutionMailTemplate.allFields)) as InstitutionMailTemplate;

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const mailVars = {
    [MailTemplateVars.TITLE]: user.title ?? '',
    [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
    [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
    [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
    ...(() => {
      if (product instanceof Workshop) return getEmailVarsFromWorkshop(product, user);
      if (product instanceof Einzelberatung) return getEmailVarsFromEinzelberatung(product, user);
      throw new Error('unreachable');
    })(),
  };

  const notificationType = getNotificationType(user, mailTemplate);
  if (notificationType === 'whatsapp') {
    await sendWhatsapp({
      to: user.whatsappPhone!,
      type: MessageType.TEMPLATE,
      content: {
        template: {
          name: getWhatsappTemplate(mailTemplateType).name,
          language: {
            code: 'de',
          },
          components: [
            {
              type: ComponentType.BODY,
              parameters: [
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapFirstNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapLastNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: product.name || '-',
                },
              ],
            },
          ],
        },
      },
    });
  } else if (notificationType === 'email') {
    const content = await mailTemplate.buildEmail(mailVars as any);
    const subject = await mailTemplate.buildSubject(mailVars as any);

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    await sendInstitutionEmail({
      email: user.email,
      subject,
      content,
      institutionID,
    });
  }
}
